/**
 * TG-5 (m1-exposure-detail-sourcing): the exposures journey's own read of
 * `OnFilePort`, independent of `App.tsx`'s `useCaptureState` snapshot — a
 * failed on-file read never blocks or invalidates the working copy the
 * rest of the journey already has (ADR-20260918-gvy0: a read has exactly
 * two outcomes, Succeeded or Failed, never a third "unknown" case the
 * rest of the journey would need to account for).
 *
 * `retry` re-issues the SAME read, through the SAME in-flight deduper
 * every other `OnFilePort` caller shares (`read-cache.ts`'s
 * `readOnFileDeduped`), so a retry clicked twice while the first attempt
 * is still in flight shares the one request rather than firing two.
 */
import { useCallback, useEffect, useState } from "react";
import { readOnFileDeduped } from "./read-cache";
import type { OnFilePort, OnFileSection } from "./on-file-port";

export type OnFileReadState =
  | { readonly status: "loading" }
  | { readonly status: "succeeded"; readonly sections: readonly OnFileSection[] }
  | { readonly status: "failed" };

export interface OnFileRead {
  readonly state: OnFileReadState;
  readonly retry: () => void;
}

export function useOnFileRead(port: OnFilePort, renewalId: string): OnFileRead {
  const [state, setState] = useState<OnFileReadState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    // Matches `App.tsx`'s own `useCaptureState` effect shape: the setState
    // calls live inside a nested async function, never directly in the
    // effect body itself (react-hooks/set-state-in-effect).
    (async () => {
      setState({ status: "loading" });
      const outcome = await readOnFileDeduped(port, renewalId);
      if (cancelled) {
        return;
      }
      if (outcome.status === "Succeeded") {
        setState({ status: "succeeded", sections: outcome.value.sections });
      } else {
        setState({ status: "failed" });
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `attempt` is the deliberate retry trigger; `port`/`renewalId` identity is stable for this journey's lifetime (App.tsx's own `useState` composition root)
  }, [renewalId, attempt]);

  const retry = useCallback(() => {
    setAttempt((count) => count + 1);
  }, []);

  return { state, retry };
}
